import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/auth/useAuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { defaultFilter } from '@/contexts/OrdersContext';
import { getOrders } from '@/services/orders';
import { hasPartnersAccess } from '@/auth/partnersAccess';
import { hasProductsV2 } from '@/utils/productsFlow';
import { getDashboardHomePath } from '@/utils/dashboard';
import { BACKGROUND_QUERY_META } from '@/utils/backgroundQuery';
// import { handleQuizNavigation } from '@/utils/quiz'; // usado pelo item Quiz (oculto)
import { isCaktoUser as checkIsCaktoUser } from '@/utils/isCaktoUser';
import { isCaktoAnalyticsEnabled, isCaktoAnalyticsProActive } from '@/utils/caktoAnalytics';
import NewFeatureChip from '@/components/new-feature-chip';
import { PRO_BLUE } from '@/sections/@dashboard/products/analytics/analyticsUtils';
import Iconify from '../../../components/iconify';
import SvgColor from '../../../components/svg-color';
import { PATH_DASHBOARD } from '../../../routes/paths';

/** Bolinha vermelha com a contagem, usada como `info` de item do nav. */
const NavCounterBadge = ({ count }) => (
  <Box
    component="span"
    sx={{
      minWidth: 20,
      height: 20,
      px: 0.5,
      borderRadius: 10,
      bgcolor: 'error.main',
      color: 'common.white',
      fontSize: 11,
      fontWeight: 700,
      lineHeight: '20px',
      textAlign: 'center',
      display: 'inline-block',
    }}
  >
    {count > 99 ? '99+' : count}
  </Box>
);

NavCounterBadge.propTypes = {
  count: PropTypes.number.isRequired,
};

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  dashboard: icon('ic_metrics'),
  mySales: icon('ic_currency'),
  collaborators: icon('ic_user'),
  banking: icon('ic_banking'),
  solarfile: icon('ic_solar_file_send_bold_duotone'),
  apps: icon('ic_external'),
  ecommerce: icon('ic_ecommerce'),
  user: icon('ic_user'),
  products: icon('ic_bag'),
  notifications: icon('ic_chat'),
  affiliates: icon('ic_affiliate'),
  subscriptions: icon('ic_dollar'),
  partners: icon('ic_new_partners'),
  quiz: icon('ic_new_quiz'),
  integrations: icon('ic_integrations_2'),
  cart: icon('ic_solar_cart'),
  funeleiro: icon('ic_funnel'),
  webhooks: icon('ic_webhook'),
  caktoApi: icon('ic_cakto'),
  caktoMcp: <Iconify icon="eva:cube-outline" sx={{ width: 1, height: 1 }} />,
  analytics: <Iconify icon="solar:chart-2-bold-duotone" sx={{ width: 1, height: 1 }} />,
  creditCards: icon('ic_card'),
  pix: icon('ic_pix'),
  bill: icon('ic_barcode'),
  ted: icon('ic_left_rifght_arrows'),
  services: icon('ic_services'),
};

const getNavConfig = (_token, user, isImpersonating, refundRequestsCount = 0) => {
  const isCaktoUser = checkIsCaktoUser(user);
  const productsListPath = hasProductsV2(user)
    ? `${PATH_DASHBOARD.general.products.root}?tab=products&flow=v2`
    : `${PATH_DASHBOARD.general.products.root}?tab=products`;

  const items = [
    { title: 'Dashboard', path: getDashboardHomePath(user), icon: ICONS.dashboard },
    {
      title: 'Produtos',
      path: productsListPath,
      icon: ICONS.products,
    },
    {
      title: 'Vitrine',
      path: PATH_DASHBOARD.general.showcase,
      icon: ICONS.cart,
    },
    {
      title: 'Minhas vendas',
      path: `${PATH_DASHBOARD.general.mySales}?tab=paid`,
      icon: ICONS.ecommerce,
      // Submenu so existe para quem esta no rollout do fluxo de reembolso 24h:
      // a mesma flag que libera a aba dentro de Minhas Vendas e o portal /refund.
      // O backend liberou o fluxo de reembolso 24h para todo produtor em 14/09/2026
      // (CKTPL-1192, #2241, "release the refund request flow to every producer"), e o
      // gate por `experimental_features` saiu de lá. Mantê-lo aqui deixava o produtor
      // recebendo pedido com janela de 24h sem tela para ver nem reverter.
      info: refundRequestsCount > 0 ? <NavCounterBadge count={refundRequestsCount} /> : null,
      hideInfoWhenOpen: true,
      children: [
        {
          title: 'Reembolso',
          path: PATH_DASHBOARD.general.mySalesRefundRequests,
          info: refundRequestsCount > 0 ? <NavCounterBadge count={refundRequestsCount} /> : null,
        },
        {
          title: 'Chargeback e MED',
          path: PATH_DASHBOARD.general.mySalesDisputes,
        },
        {
          title: 'Relatórios',
          path: PATH_DASHBOARD.general.mySalesReports,
        },
        {
          title: 'Cupons',
          path: PATH_DASHBOARD.general.mySalesCoupons,
        },
      ],
    },
    {
      title: 'Assinaturas',
      path: `${PATH_DASHBOARD.general.subscriptions}?tab=active`,
      icon: ICONS.subscriptions,
    },
  ];

  // Relatórios e Cupons moram no submenu de Minhas vendas (CKTPL-1534), e Equipe no
  // menu da foto de perfil (CKTPL-1533). A busca do topo lê esta lista e soma o
  // atalho de Equipe (`Searchbar.js`).
  items.push({
    title: 'Afiliados',
    path: `${PATH_DASHBOARD.general.affiliates}?tab=active`,
    icon: ICONS.user,
  });

  if (!isImpersonating) {
    items.push({
      title: 'Financeiro',
      path: PATH_DASHBOARD.general.financial,
      icon: ICONS.banking,
    });
  }

  if (!isImpersonating && isCaktoUser) {
    items.push({
      title: 'Banking',
      path: PATH_DASHBOARD.general.banking.root,
      icon: ICONS.banking,
      children: [
        {
          title: 'Boleto',
          path: '',
          icon: ICONS.bill,
          depth: 2,
          children: [
            // "Emitir" removido em 21/08/2026: a emissao de boleto pela conta
            // digital esta bloqueada no backend por kill switch
            // (baas_billet_emission_enabled). Reexibir junto com a reativacao.
            {
              title: 'Pagar',
              path: PATH_DASHBOARD.general.banking.payBoleto,
              depth: 3,
            },
            {
              title: 'Assistente de Boletos',
              path: PATH_DASHBOARD.general.banking.boletoAssistant,
              depth: 3,
            },
          ],
        },
        {
          title: 'Cartão',
          path: PATH_DASHBOARD.general.banking.creditCards,
          icon: ICONS.creditCards,
          depth: 2,
        },
        {
          title: 'TED',
          path: PATH_DASHBOARD.general.banking.ted,
          icon: ICONS.ted,
          depth: 2,
        },
        {
          title: 'Serviços',
          path: '',
          icon: ICONS.services,
          depth: 2,
          children: [
            {
              title: 'Recarga de Celular',
              path: PATH_DASHBOARD.general.banking.mobileRecharge,
              depth: 3,
            },
            {
              title: 'Gift Card',
              path: PATH_DASHBOARD.general.banking.giftCard,
              depth: 3,
            },
          ],
        },
      ],
    });
  }

  const integrationsChildren = [
    { title: 'Apps', path: PATH_DASHBOARD.general.apps, icon: ICONS.apps },
    { title: 'Webhooks', path: PATH_DASHBOARD.general.webhooks, icon: ICONS.webhooks },
    ...(!isImpersonating
      ? [
          { title: 'Cakto API', path: PATH_DASHBOARD.general.caktoApi, icon: ICONS.caktoApi },
          { title: 'Cakto MCP', path: PATH_DASHBOARD.general.caktoMcp, icon: ICONS.caktoMcp },
        ]
      : []),
  ];

  // `nfseEnabled` vem recalculado pós-cache pelo `/user/`; `experimental_features`
  // congela por uma hora no payload cacheado e deixaria o item visível para quem
  // acabou de sair da audiência.
  if (!isImpersonating && user?.nfseEnabled) {
    items.push({
      title: 'Nota Fiscal',
      path: '#',
      icon: ICONS.solarfile,
      children: [
        // `exact`: a avulsa mora sob /fiscal/invoices e tem item próprio; sem ele, os dois
        // acenderiam juntos na tela da avulsa.
        { title: 'Dashboard', path: PATH_DASHBOARD.fiscal.financialInvoices, exact: true },
        // A avulsa no painel segue a liberação do MCP (`nfseAvulsaEnabled`).
        ...(user?.nfseAvulsaEnabled
          ? [{ title: 'Emitir nota avulsa', path: PATH_DASHBOARD.fiscal.financialInvoicesAvulsa }]
          : []),
        { title: 'Configurações', path: PATH_DASHBOARD.fiscal.settings },
      ],
    });
  }

  items.push({
    title: 'Integrações',
    path: '#',
    icon: ICONS.integrations,
    children: integrationsChildren,
  });

  // Logo abaixo de Integrações, por decisão de produto (27/10). Mesma liberação da tela do
  // produto: quem não tem o Analytics cairia no 403 da guarda. O Pro vem incluso na conta (27/10),
  // e o menu leva o nome do produto.
  if (isCaktoAnalyticsEnabled(user)) {
    items.push({
      title: isCaktoAnalyticsProActive(user) ? 'Analytics Pro' : 'Analytics',
      path: PATH_DASHBOARD.general.analytics,
      icon: ICONS.analytics,
      // Selo de lançamento do Pro incluso, no azul da vitrine dele.
      info: isCaktoAnalyticsProActive(user) ? (
        <NewFeatureChip sx={{ bgcolor: PRO_BLUE, color: 'common.white' }} />
      ) : null,
    });
  }

  if (hasPartnersAccess(user)) {
    items.push({
      title: 'Cakto Partners',
      path: `${PATH_DASHBOARD.general.partners}?tab=indique-e-lucre`,
      icon: ICONS.partners,
    });
  }

  // Ocultados temporariamente do menu (podem retornar) - 2026-06-24
  // items.push(
  //   {
  //     title: 'Quiz',
  //     icon: ICONS.quiz,
  //     onClick: handleQuizNavigation,
  //   },
  //   {
  //     title: 'Funeleiro',
  //     path: 'https://app.funeleiro.com.br',
  //     icon: ICONS.funeleiro,
  //     isExternalLink: true,
  //   }
  // );

  return [{ items }];
};

const Navigation = () => {
  const [token, setToken] = useState('');
  const { user } = useAuthContext();
  const { impersonatedUserId } = useImpersonation();
  const isImpersonating = !!impersonatedUserId;

  const { data: { count: refundCount = 0 } = {} } = useQuery({
    queryKey: ['orders', 'refundRequestsCount'],
    queryFn: () => getOrders({ page: 1, status: 'refund_requested', filter: defaultFilter }),
    initialData: { count: 0 },
    refetchInterval: 60000,
    meta: BACKGROUND_QUERY_META,
  });

  useEffect(() => {
    setToken(localStorage.getItem('accessToken') || '');
  }, []);

  return getNavConfig(token, user, isImpersonating, refundCount);
};

export default Navigation;
